import { CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, Input } from '@angular/core';
import { ButtonComponent, IconComponent } from '@mode/capra';
import { HelpDocsService, getAppName, getThoughtspotIntegrated } from '@mode/shared/util-js';

interface Section {
  icon: string;
  header: string;
  headerTestAttribute: string;
  body: string;
  bodyTestAttribute: string;
  buttons: Button[];
}

interface Button {
  text: string;
  clickAction: (e: Event) => void;
  testAttribute: string;
  class?: string;
}

@Component({
  standalone: true,
  imports: [CommonModule, ButtonComponent, IconComponent],
  selector: 'mode-discover-main-content',
  templateUrl: './discover-main-content.component.html',
  styleUrls: ['./discover-main-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiscoverMainContentComponent {
  @Input() username = '';

  public readonly appName = getAppName();
  public readonly sections: Section[] = [];

  private readonly thoughtspotIntegrated = getThoughtspotIntegrated(this.document) ?? false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    public helpDocs: HelpDocsService
  ) {
    this.sections = this.getSections();
  }

  private getSections(): Section[] {
    const connectADatabase = {
      icon: 'illo-connect-database',
      header: 'Connect a Database',
      headerTestAttribute: 'connect-a-database-header',
      body: `Whether you’re just getting started with a small production database or a massive company warehouse,
      you can connect it to ${this.appName}. Once connected, you and any colleagues you invite to this Workspace will be able to use it.`,
      bodyTestAttribute: 'connect-a-database-body',
      buttons: [
        {
          text: 'Connect a Database',
          clickAction: (e: Event) => this.navigateToURL(e, `/${this.username}/data_sources/select`),
          testAttribute: 'connect-database-button',
        },
      ],
    };

    const buildADataset = {
      icon: 'illo-query-editor',
      header: 'Analyze data',
      headerTestAttribute: 'analyze-data-header',
      body: `Build a Dataset to import SQL query results as a table in ThoughtSpot data workspace, or create a Report for further analysis, with the Notebook and visualizations available for deeper insights.`,
      bodyTestAttribute: 'analyze-data-body',
      buttons: [
        {
          text: 'Build a Dataset',
          clickAction: (e: Event) => this.navigateToURL(e, `/editor/${this.username}/datasets/new`),
          testAttribute: 'build-dataset-button',
          class: 'use-dataset-button',
        },
        {
          text: 'Write SQL',
          clickAction: (e: Event) => this.navigateToURL(e, `/editor/${this.username}/reports/new`),
          testAttribute: 'write-sql-button',
          class: 'sql-button',
        },
      ],
    };

    const useADataset = {
      icon: 'illo-query-editor',
      header: 'Analyze data',
      headerTestAttribute: 'analyze-data-header',
      body: `Reports are the heart and soul of the ${this.appName} product, and allow you to create and share analysis with others.
        Write SQL to analyze warehouse data directly, or use Datasets to start with a pre-made data table.
        Don't see Datasets yet? Talk to your Data team about getting this feature enabled!`,
      bodyTestAttribute: 'analyze-data-body',
      buttons: [
        {
          text: 'Use a Dataset',
          clickAction: (e: Event) => this.navigateToURL(e, `/editor/reports/new`),
          testAttribute: 'use-dataset-button',
          class: 'use-dataset-button',
        },
        {
          text: 'Write SQL',
          clickAction: (e: Event) => this.navigateToURL(e, `/editor/${this.username}/reports/new`),
          testAttribute: 'write-sql-button',
          class: 'sql-button',
        },
      ],
    };

    const exploreASampleReport = {
      icon: 'illo-dashboards',
      header: 'Explore sample analysis',
      headerTestAttribute: 'explore-sample-analysis-header',
      body: `Dive into an existing Report for a powerful jumping off point.
      Answer ad-hoc questions about your aspect of the business by interacting with the Report or exploring individual visualizations.`,
      bodyTestAttribute: 'explore-sample-analysis-body',
      buttons: [
        {
          text: 'Explore a sample Report',
          clickAction: (e: Event) => this.navigateToURL(e, '/demo/reports/8c24e9c20a64', 'https://app.mode.com'),
          testAttribute: 'explore-sample-report-button',
        },
      ],
    };

    const inviteYourTeam = {
      icon: 'illo-invite',
      header: 'Invite your team',
      headerTestAttribute: 'invite-your-team-header',
      body: `Analysis is better with your colleagues. Start the collaboration by inviting them to ${this.appName}.`,
      bodyTestAttribute: 'invite-your-team-body',
      buttons: [
        {
          text: 'Send Invites',
          clickAction: (e: Event) => this.navigateToURL(e, `/${this.username}/invites/new`),
          testAttribute: 'send-invites-button',
        },
      ],
    };

    const checkOutTheDocs = {
      icon: 'illo-docs',
      header: 'Ready for more?',
      headerTestAttribute: 'ready-for-more-header',
      body: `Check out our Help Site for guides to get started and detailed documentation on everything from building Reports, creating visualizations, doing advanced analysis, setting up ${this.appName} as an admin, and more!`,
      bodyTestAttribute: 'ready-for-more-body',
      buttons: [
        {
          text: 'Check out the docs',
          clickAction: (e: Event) => {
            if (this.thoughtspotIntegrated) {
              window.open(this.helpDocs.links['https://mode.com/help/get-started'], '_blank');
            } else {
              this.navigateToURL(e, '/help');
            }
          },
          testAttribute: 'check-out-docs-button',
        },
      ],
    };

    const modeSections = [connectADatabase, useADataset, exploreASampleReport, inviteYourTeam, checkOutTheDocs];
    const analystStudioSections = [connectADatabase, buildADataset, checkOutTheDocs];

    return this.thoughtspotIntegrated ? analystStudioSections : modeSections;
  }

  navigateToURL(evt: Event, urlPrefix: string, origin = window.location.origin): void {
    window.open(`${origin}${urlPrefix}`, '_blank');
    if (evt.target) {
      (evt.target as HTMLElement).blur();
    }
  }
}
